import React from "react";

const marketLabelKey = (name) => String(name || "").toLowerCase().replace(/[^a-z0-9]/g, "").replace("favorite", "favourite");

// A presentation catalog only; missing entries never enter the betting data.
export const buildSupplementalMarkets = ({
  isCricket = false, primaryMarketId, primaryMarketName = "Match Odds",
  marketList = [], extraMarkets = [], bookmakerMarkets = [], tieRunners = [],
}) => {
  const panels = new Map();
  const addMarket = (market, live = false) => {
    const name = market?.marketName || market?.market;
    if (!name) return;
    const labelKey = marketLabelKey(name);
    const id = market.marketId == null ? "" : String(market.marketId);
    if ((id && id === String(primaryMarketId)) || (!id && labelKey === marketLabelKey(primaryMarketName))) return;
    const key = ["tiedmatch", "lunchfavourite"].includes(labelKey)
      ? labelKey : `market:${id || labelKey}`;
    const previous = panels.get(key);
    const runners = market.selections?.length ? market.selections : market.jsonData;
    panels.set(key, {
      ...previous, key, name, bookmaker: market.marketType === "bookmaker" || labelKey === "lunchfavourite",
      runners: runners?.length ? runners : previous?.runners || [],
      min: market.min ?? market.minAmount ?? previous?.min,
      max: market.max ?? market.maxAmount ?? previous?.max,
      ...(live ? { liveMarket: market } : {}),
      ...(market.marketType === "bookmaker" ? { quoteMarket: market } : {}),
    });
  };
  marketList.forEach((market) => addMarket(market));
  extraMarkets.forEach((market) => addMarket(market, true));
  bookmakerMarkets.forEach((market) => addMarket(market));

  if (isCricket) {
    [
      { key: "tiedmatch", name: "Tied Match", runners: tieRunners.length ? tieRunners : ["Yes", "No"] },
      { key: "lunchfavourite", name: "Lunch Favourite", runners: ["Lunch Favourite", "Lunch Non-Favourite"], bookmaker: true },
    ].forEach((fallback) => {
      if (marketLabelKey(primaryMarketName) === fallback.key) return;
      const existing = panels.get(fallback.key);
      panels.set(fallback.key, existing
        ? { ...existing, runners: existing.runners.length ? existing.runners : fallback.runners }
        : fallback);
    });
  }

  return [...panels.values()].sort((a, b) => Number(b.key === "tiedmatch") - Number(a.key === "tiedmatch"));
};

/**
 * MarketTabs
 * ----------------------------------------------------------------------------
 * Reference-style horizontal pill bar shown under the "Cricket" title bar
 * (e.g. All / Popular / Match Odds / Tied Match / Bookmaker ...). Scrolls
 * horizontally. The first pill ("All") is green/active by default.
 *
 * Mirrors the reference exchange's .marketDetailsList pills:
 *   pill: font-weight 700, rounded, dark bg, 1px border
 *   active: green bg + white text
 *
 * Props:
 *   tabs      - array of { key, label }
 *   active    - currently selected key
 *   onSelect  - (key) => void
 */
const MarketTabs = ({ tabs = [], active, onSelect }) => {
  return (
    <>
      <style>{mtStyles}</style>
      <div className="mkt-tabs-wrap">
        <ul className="mkt-tabs">
          {tabs.map((t) => (
            <li key={t.key}>
              <a
                href="javascript:void(0)"
                className={active === t.key ? "active" : ""}
                onClick={() => onSelect && onSelect(t.key)}
              >
                {t.label}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </>
  );
};

const mtStyles = `
.mkt-tabs-wrap{
  background:#e6ebee;
  padding:1vw 2.4vw;
  overflow-x:auto;
  -webkit-overflow-scrolling:touch;
}
.mkt-tabs-wrap::-webkit-scrollbar{ display:none; }
.mkt-tabs{
  display:flex;
  gap:1.2vw;
  margin:0;
  padding:0;
  list-style:none;
  white-space:nowrap;
  width:max-content;
}
.mkt-tabs li{ list-style:none; }
.mkt-tabs a{
  display:inline-block;
  line-height:9.2vw;
  padding:0 3vw;
  font-size:3.9vw;
  font-weight:700;
  color:#fff;
  text-decoration:none;
  background:#2b4150;
  border:1px solid #1e2f3a;
  border-radius:5vw;
}
/* first pill ("All") is a perfect circle */
.mkt-tabs li:first-child a{
  width:9.2vw;
  height:9.2vw;
  line-height:9.2vw;
  padding:0;
  text-align:center;
  border-radius:50%;
}
.mkt-tabs a.active{
  background:#7ec13d;      /* green active pill */
  color:#000;             /* black text */
  border-color:#000;      /* black border */
}
`;

export const BetCategoryTabs = ({ tabs = [], active = "All", onSelect, label, variant = "fancy" }) => (
  <div
    id="fancyBetTabWrap"
    role="group"
    aria-label={label}
    style={{
      display: "flex",
      justifyContent: "center",
      width: "100%",
      boxSizing: "border-box",
      margin: 0,
      padding: "4px 20px",
      background: variant === "sportsbook"
        ? "linear-gradient(180deg, #ff6c17 0%, #d94c00 100%)"
        : "linear-gradient(180deg, #0a92a5 0%, #087989 100%)",
    }}
  >
    <div
      style={{
        display: "flex",
        flexWrap: "nowrap",
        width: "max-content",
        maxWidth: "100%",
        minWidth: 0,
        height: "22px",
        margin: 0,
        padding: 0,
        gap: 0,
        overflowX: "auto",
        scrollbarWidth: "none",
        borderRadius: "5px",
        background: variant === "sportsbook" ? "#ffb18a" : "#98c4ca",
      }}
    >
      {tabs?.map((tab) => (
        <button
          key={tab.type}
          type="button"
          aria-pressed={active == tab.type}
          onClick={() => onSelect?.(tab.type)}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flex: "0 0 auto",
            height: "22px",
            margin: 0,
            padding: "0 7px",
            border: 0,
            borderRadius: "5px",
            background: active == tab.type ? "#fff" : "transparent",
            boxShadow: "none",
            color: active == tab.type ? "#000" : "#243238",
            fontFamily: "Tahoma, Helvetica, sans-serif",
            fontSize: "12px",
            fontWeight: 700,
            lineHeight: "22px",
            letterSpacing: 0,
            whiteSpace: "nowrap",
            cursor: onSelect ? "pointer" : "default",
          }}
        >
          {tab.name}
        </button>
      ))}
    </div>
  </div>
);

export default MarketTabs;
